import React from 'react';
import './Privacy.css';

const Privacy: React.FC = () => {
  return (
    <div className="privacy-page">
      <div className="privacy-container">
        <h1 className="privacy-title">Privacy Policy</h1>
        <p className="privacy-updated">Last Updated: April 21, 2026</p>

        <section className="privacy-section">
          <h2>Introduction</h2>
          <p>
            At ChowMeDown, we respect your privacy and are committed to protecting your personal information. 
            This Privacy Policy explains what data we collect, why we collect it, and how we use it to provide 
            you with personalized dog meal recipes and nutrition guidance.
          </p>
          <p>
            ChowMeDown is operated by <strong>ChowMeDown LLC</strong>, a limited liability company registered 
            in Wyoming, United States. The business is operated from France.
          </p>
          <p>
            ChowMeDown LLC is the data controller responsible for your personal data.
          </p>
        </section>

        <section className="privacy-section">
          <h2>What Data We Collect</h2>
          <p>We collect the following types of information:</p>
          
          <h3>Account Information</h3>
          <ul>
            <li><strong>Email address:</strong> Used for account creation, login, and communication</li>
            <li><strong>Name:</strong> First and last name for personalization</li>
            <li><strong>Password:</strong> Securely encrypted and stored</li>
          </ul>

          <h3>Dog Profile Information</h3>
          <ul>
            <li><strong>Dog's name, breed, age, and weight</strong></li>
            <li><strong>Body condition and activity level</strong></li>
            <li><strong>Allergies and dietary restrictions</strong></li>
            <li><strong>Meals per day and feeding preferences</strong></li>
          </ul>

          <h3>Usage Data</h3>
          <ul>
            <li><strong>Recipe views and favorites:</strong> To personalize recommendations</li>
            <li><strong>Ingredient preferences:</strong> For custom recipe generation</li>
            <li><strong>Device and browser information:</strong> For website optimization</li>
            <li><strong>IP address and location data:</strong> For security and analytics</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>Why We Collect This Data</h2>
          <p>We use your information to:</p>
          <ul>
            <li>Create and manage your account</li>
            <li>Generate personalized recipe suggestions and feeding guidance for your dog</li>
            <li>Calculate accurate portion sizes based on your dog's specific needs</li>
            <li>Save your favorite recipes and preferences</li>
            <li>Provide customer support and respond to your inquiries</li>
            <li>Process payments for premium features</li>
            <li>Improve our services and develop new features</li>
            <li>Send important updates about your account or our services</li>
            <li>Ensure the security of your account and prevent fraud</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>Third-Party Services</h2>
          <p>We work with trusted third-party service providers to operate our platform:</p>
          
          <div className="third-party-item">
            <h3>Stripe</h3>
            <p>
              We use Stripe for secure payment processing. Your payment information is handled directly by Stripe 
              and is never stored on our servers. Stripe is PCI-DSS compliant and maintains the highest security standards.
            </p>
          </div>

          <div className="third-party-item">
            <h3>Hosting & Infrastructure</h3>
            <p>
              Our application is hosted on secure cloud infrastructure. These providers may have access to server 
              logs and technical data necessary to maintain service uptime and security.
            </p>
          </div>

          <div className="third-party-item">
            <h3>Analytics</h3>
            <p>
              We may use third-party analytics tools to understand how users interact with our website. This helps us 
              improve user experience and identify areas for enhancement.
            </p>
          </div>

          <p className="third-party-note">
            We do not sell, rent, or share your personal information with third parties for their marketing purposes.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Data Retention</h2>
          <p>
            We retain your personal data for as long as your account is active or as needed to provide our services. 
            If you delete your account, we will delete or anonymize your data within a reasonable timeframe, unless 
            we are required to retain it for legal or regulatory reasons.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Legal Basis for Processing</h2>
          <p>We process your personal data based on the following legal grounds:</p>
          <ul>
            <li><strong>Performance of a contract:</strong> to provide our services and manage your account</li>
            <li><strong>Legitimate interests:</strong> to improve our services and ensure security</li>
            <li><strong>Consent:</strong> where required, such as for certain communications</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>International Data Transfers</h2>
          <p>
            Your data may be processed outside of your country, including in the United States, where our service 
            providers operate. We ensure appropriate safeguards are in place to protect your data.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Data Security</h2>
          <p>
            We implement industry-standard security measures to protect your data:
          </p>
          <ul>
            <li>Encryption of data in transit using HTTPS/SSL</li>
            <li>Secure password hashing and storage</li>
            <li>Regular security audits and updates</li>
            <li>Access controls and authentication requirements</li>
            <li>Secure database storage with backup systems</li>
          </ul>
          <p>
            While we strive to protect your information, no method of transmission over the internet is 100% secure. 
            We cannot guarantee absolute security but are committed to protecting your data to the best of our ability.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Your Rights</h2>
          <p>You have the following rights regarding your personal data:</p>
          
          <h3>Access Your Data</h3>
          <p>You can view and update your account information and dog profiles at any time through your account settings.</p>

          <h3>Delete Your Data</h3>
          <p>
            You have the right to request deletion of your account and all associated data. To delete your account:
          </p>
          <ol>
            <li>Log in to your account</li>
            <li>Navigate to Settings</li>
            <li>Select "Delete Account" at the bottom of the page</li>
            <li>Confirm your decision</li>
          </ol>
          <p>
            Alternatively, you can email us at <a href="mailto:chowmedown321@gmail.com">chowmedown321@gmail.com</a> to 
            request account deletion. We will process your request within 30 days.
          </p>

          <h3>Data Portability</h3>
          <p>
            You can request a copy of your data in a machine-readable format. Contact us at the email below to 
            submit a data export request.
          </p>

          <h3>Opt-Out of Communications</h3>
          <p>
            You can unsubscribe from promotional emails at any time by clicking the unsubscribe link in any email 
            or by updating your email preferences in account settings.
          </p>

          <h3>Object to or Restrict Processing</h3>
          <p>
            You have the right to object to or request restriction of certain types of data processing under applicable laws.
          </p>

          <h3>Lodge a Complaint</h3>
          <p>
            You also have the right to lodge a complaint with your local data protection authority if you believe your 
            data has been handled improperly.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Cookies and Tracking</h2>
          <p>
            We use cookies to maintain your session, remember preferences, and analyze usage. Where required, 
            we will request your consent before placing non-essential cookies. You can control cookie settings 
            through your browser, though some features may not function properly if cookies are disabled.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Children's Privacy</h2>
          <p>
            ChowMeDown is not intended for children under 13 years of age. We do not knowingly collect personal 
            information from children. If you believe we have inadvertently collected data from a child, please 
            contact us immediately.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices or legal 
            requirements. We will notify you of any material changes by posting the updated policy on this page 
            and updating the "Last Updated" date. If required, we will notify users of significant changes via 
            email or within the app. Your continued use of our services after such updates indicates your 
            acceptance of the revised policy.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Contact Us</h2>
          <p>
            If you have questions, concerns, or requests regarding this Privacy Policy or your personal data, 
            please contact us:
          </p>
          <div className="contact-info">
            <p><strong>Email:</strong> <a href="mailto:chowmedown321@gmail.com">chowmedown321@gmail.com</a></p>
            <p><strong>Instagram:</strong> <a href="https://instagram.com/chowmedown_" target="_blank" rel="noopener noreferrer">@chowmedown_</a></p>
            <p><strong>Location:</strong> France</p>
          </div>
          <p>
            We will respond to your inquiry within 7 business days.
          </p>
        </section>
      </div>
    </div>
  );
};

export default Privacy;
